/**
 * OrgDrawerContent: body of the AdminDrawer (a Carbon SidePanel) for an
 * Organization row.
 *
 * Renders the related pivots, detail fields, a recent-activity stub, and the
 * action list (drawer-parts). The drawer shell (header, Close, slide-in) is
 * owned by AdminDrawer; this component is just the children.
 *
 * State ownership:
 *   - Mutations (update / deactivate / approve) live in `useOrganizations`
 *     and are passed in by the parent. We don't re-fetch here.
 *   - The Edit OrgFormModal is mounted INSIDE this component because the
 *     "open" / "close" lifecycle is local to the drawer's action list.
 *     Save goes back through the parent's `updateOrganization` so the
 *     React Query cache stays the single source of truth.
 *   - Approve and Deactivate ask first in a Carbon ConfirmDialog (Deactivate
 *     as danger), which stays open and busy until the mutation settles.
 *
 * Voice:
 *   - Imperative action labels ("Edit", "Invite admin", "Connect tenant"),
 *     never noun phrases.
 *   - "Average posture" + "Recent activity" are honest stubs, no fabricated
 *     metrics. "Open as workspace" is disabled with "Not available yet" as
 *     its hover text.
 *
 * Field-name notes vs. the spec prompt:
 *   - `OrganizationListItemDto` exposes `linkedTenantCount` /
 *     `linkedUserCount`, not `tenantCount` / `userCount` — we use the real
 *     names.
 *   - No `owner` field on the DTO; the closest analogues are
 *     `primaryContactName` / `primaryContactEmail`.
 *   - No `lastActivity` field on the DTO; the row carries `updatedAt`,
 *     which is the closest honest substitute.
 */

import { useMemo, useState } from 'react'
import toast from 'react-hot-toast'

import type {
  CreateOrganizationRequest,
  OrganizationListItemDto,
  UpdateOrganizationRequest,
} from '@/lib/api'

import OrgFormModal from '@/components/OrganizationManagement/OrgFormModal'
import AddUserModal from '@/components/AddUserModal'
import { ConfirmDialog } from '@/components/carbon/ConfirmDialog'
import PivotChip from '@/components/AdminPortal/PivotChip'
import {
  ActionList,
  ActionRow,
  DetailGrid,
  DrawerSection,
  MonoId,
  Muted,
} from '@/components/AdminPortal/drawer-parts'
import { countLabel, formatLongDate, shortenId } from '@/components/AdminPortal/admin-format'
import { orgDecisionCopy, type OrgDecision } from '@/components/AdminPortal/confirm-copy'

/* -------------------------------------------------------------------------- */
/* Props                                                                      */
/* -------------------------------------------------------------------------- */

export interface OrgDrawerContentProps {
  org: OrganizationListItemDto
  onClose(): void
  onPivot(target: 'tenants' | 'people', orgId: string): void
  updateOrganization(
    id: string,
    data: Partial<UpdateOrganizationRequest>,
  ): Promise<OrganizationListItemDto>
  deactivateOrganization(id: string, reason?: string): Promise<void>
  approveOrganization(
    id: string,
    opts?: { allowAutoProvision?: boolean },
  ): Promise<OrganizationListItemDto>
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function OrgDrawerContent({
  org,
  onClose,
  onPivot,
  updateOrganization,
  deactivateOrganization,
  approveOrganization,
}: OrgDrawerContentProps) {
  const [showEditModal, setShowEditModal] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [busyAction, setBusyAction] = useState<
    null | 'approve' | 'deactivate'
  >(null)
  // The decision the ConfirmDialog is asking about; it stays open (busy) until the mutation settles.
  const [confirm, setConfirm] = useState<OrgDecision | null>(null)

  const lastActivity = useMemo(() => formatLongDate(org.updatedAt), [org.updatedAt])
  const shortId = useMemo(() => shortenId(org.id), [org.id])

  const handleSaveEdit = async (
    data: CreateOrganizationRequest | Partial<UpdateOrganizationRequest>,
  ) => {
    setIsSaving(true)
    try {
      await updateOrganization(org.id, data as Partial<UpdateOrganizationRequest>)
      setShowEditModal(false)
    } finally {
      setIsSaving(false)
    }
  }

  // A rejected request tells the user and leaves the dialog open (the button
  // is live again), so it never closes as if the decision had gone through.
  const handleApprove = async () => {
    setBusyAction('approve')
    const done = await settleOrgDecision(
      () => approveOrganization(org.id, { allowAutoProvision: true }),
      `Couldn't approve ${org.name}. Try again.`,
    )
    setBusyAction(null)
    if (done) setConfirm(null)
  }

  const handleDeactivate = async () => {
    setBusyAction('deactivate')
    const done = await settleOrgDecision(
      () => deactivateOrganization(org.id),
      `Couldn't deactivate ${org.name}. Try again.`,
    )
    setBusyAction(null)
    if (done) {
      setConfirm(null)
      onClose()
    }
  }

  const showApprove = org.status === 'PendingApproval'
  const showDeactivate = org.status !== 'Inactive'

  const ownerLabel = composeOwnerLabel(
    org.primaryContactName,
    org.primaryContactEmail,
  )

  return (
    <div className="flex flex-col gap-6">
      {/* ── Related: the pivots to the other tabs ──────────────────────── */}
      <DrawerSection title="Related" first>
        <DetailGrid
          items={[
            {
              label: 'Tenants',
              value: (
                <PivotChip
                  label={countLabel(org.linkedTenantCount, 'tenant', 'tenants')}
                  onClick={() => onPivot('tenants', org.id)}
                />
              ),
            },
            {
              label: 'People',
              value: (
                <PivotChip
                  label={countLabel(org.linkedUserCount, 'person', 'people')}
                  onClick={() => onPivot('people', org.id)}
                />
              ),
            },
            { label: 'Average posture', value: <Muted>Not available yet</Muted> },
          ]}
        />
      </DrawerSection>

      {/* ── Detail fields ──────────────────────────────────────────────── */}
      <DrawerSection title="Details">
        <DetailGrid
          items={[
            { label: 'Slug', value: <span className="font-mono">{org.slug}</span> },
            { label: 'ID', value: <MonoId value={org.id} short={shortId} /> },
            { label: 'Owner', value: ownerLabel ?? <Muted>Not recorded</Muted> },
            { label: 'Parent org', value: org.parentOrganizationName ?? <Muted>No parent</Muted> },
            { label: 'Billing email', value: org.primaryContactEmail ?? <Muted>Not recorded</Muted> },
            { label: 'Last activity', value: lastActivity ?? <Muted>Not recorded</Muted> },
          ]}
        />
      </DrawerSection>

      {/* ── Recent activity stub ───────────────────────────────────────── */}
      <DrawerSection title="Recent activity">
        <p className="m-0 text-body text-text-helper">Recent activity will appear here.</p>
      </DrawerSection>

      {/* ── Actions ────────────────────────────────────────────────────── */}
      <DrawerSection title="Actions">
        <ActionList>
          <ActionRow
            label="Edit"
            description="Update name, slug, access, and contact details."
            onClick={() => setShowEditModal(true)}
          />
          <ActionRow
            label="Invite admin"
            description="Send a Guardian invite to a person in this organization."
            onClick={() => setShowInviteModal(true)}
          />
          <ActionRow
            label="Open as workspace"
            description="Switch context to this organization."
            disabled
            title="Not available yet"
          />
          <ActionRow
            label="Connect tenant"
            description="Jump to Tenants pre-filtered to this organization."
            onClick={() => onPivot('tenants', org.id)}
          />

          {showApprove && (
            <ActionRow
              label="Approve"
              description="Activate this organization and let users sign in."
              onClick={() => setConfirm('approve')}
              disabled={busyAction !== null}
            />
          )}
          {showDeactivate && (
            <ActionRow
              label="Deactivate"
              description="Block sign-in for this organization. Reversible."
              onClick={() => setConfirm('deactivate')}
              disabled={busyAction !== null}
              danger
            />
          )}
        </ActionList>
      </DrawerSection>

      {/* ── Mounted modals ─────────────────────────────────────────────── */}
      {showEditModal && (
        <OrgFormModal
          open={showEditModal}
          mode="edit"
          initial={org}
          msps={[]}
          onSave={handleSaveEdit}
          onClose={() => setShowEditModal(false)}
          isSaving={isSaving}
        />
      )}

      <AddUserModal
        isOpen={showInviteModal}
        organization={{
          id: org.id,
          name: org.name,
          type: org.type,
          azureAdTenantId: org.azureAdTenantId,
        }}
        onClose={() => setShowInviteModal(false)}
      />

      {confirm && (
        <ConfirmDialog
          open
          {...orgDecisionCopy(confirm, org.name)}
          loading={busyAction === confirm}
          onConfirm={() => void (confirm === 'approve' ? handleApprove() : handleDeactivate())}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Pure helpers                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Runs an Approve or Deactivate request. A rejection becomes a toast with the
 * given editorial sentence (never the raw error) and resolves false, so the
 * caller keeps its confirm dialog open.
 */
export async function settleOrgDecision(run: () => Promise<unknown>, failure: string): Promise<boolean> {
  try {
    await run()
    return true
  } catch {
    toast.error(failure)
    return false
  }
}

function composeOwnerLabel(
  name: string | null | undefined,
  email: string | null | undefined,
): string | null {
  if (name && email) return `${name} (${email})`
  return name ?? email ?? null
}
